import type { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import type { CameraInterface } from "./Engine";
import type { CSS2DRenderer } from "./renderer/CSS2DRenderer";
import type { CSS3DRenderer } from "./renderer/CSS3DRenderer.js";
import type { IRenderer } from "./renderer/IRenderer";
import { renderSettingsStore } from "../core/pipeline/RenderSettingsStore";

type EngineResizeSize = {
  width: number;
  height: number;
};

export type EngineResizeControllerOptions = {
  renderer: IRenderer;
  cssRenderer: CSS2DRenderer;
  cssRenderer3d: CSS3DRenderer;
  getBindElement: () => HTMLElement | undefined;
  getCamera: () => CameraInterface | undefined;
  getComposer: () => EffectComposer | undefined;
  getSize: () => EngineResizeSize;
  setSize: (width: number, height: number) => void;
  setCssRendererAdded: (value: boolean) => void;
  setCssRenderer3dAdded: (value: boolean) => void;
  requestUpdate: () => void;
  dispatchResize: () => void;
};

/**
 * Live window scaling must not assign canvas.width: that clears an alpha
 * buffer and the host behind it (#f3f4f6) paints as a white blink. Stretch
 * the last frame with CSS until the size settles, then snapshot-cover,
 * resize the drawing buffer, and uncover after the next presented frame.
 */
export class EngineResizeController {
  private static readonly _SETTLE_MS = 80;

  private _resizeObserver?: ResizeObserver;
  private _observedElement?: HTMLElement;
  private _resizeInitialized = false;
  private _isResizing = false;
  private _pendingObservedWidth = 0;
  private _pendingObservedHeight = 0;
  private _hasPendingObservedSize = false;
  private _commitAllowed = false;
  private _settleTimer = 0;
  private _coverCanvas?: HTMLCanvasElement;
  private _coverUntilPresented = false;

  constructor(private readonly _options: EngineResizeControllerOptions) {}

  bind(element: HTMLElement | undefined): void {
    if (this._observedElement) {
      this._resizeObserver?.unobserve(this._observedElement);
      this._observedElement = undefined;
    }

    this.resize();

    if (!this._resizeObserver) {
      this._resizeObserver = new ResizeObserver((entries) => {
        const cr = entries[0]?.contentRect;
        if (cr && cr.width > 0 && cr.height > 0) {
          this._scheduleObservedResize(cr.width, cr.height);
        }
      });
    }

    if (element) {
      this._resizeObserver.observe(element);
      this._observedElement = element;
    }

    this.prepareRendererDomElements();
    this.appendRendererDomElements();
  }

  resize(force = true): void {
    if (this._isResizing) return;
    this._isResizing = true;

    try {
      const { width, height } = this._consumeResizeSize();
      const current = this._options.getSize();
      const sizeChanged =
        !this._resizeInitialized ||
        width !== current.width ||
        height !== current.height;

      if (!force && !sizeChanged) {
        return;
      }

      this._options.setSize(width, height);
      this._resizeInitialized = true;

      const renderer = this._options.renderer.instance;
      this._options.cssRenderer.setSize(width, height);
      this._options.cssRenderer3d.setSize(width, height);
      const pixelRatio = renderSettingsStore.resolvePixelRatio();
      this._coverWithLastFrame();
      renderer.setDrawingBufferSize(width, height, pixelRatio);
      this._coverUntilPresented = true;
      this._options.getCamera()?.update(width, height);

      this._ensureRendererDomAttached();

      const composer = this._options.getComposer();
      if (composer) {
        const pixelRatio = renderer.getPixelRatio();
        composer.setSize(width, height);
        composer.setPixelRatio(pixelRatio);
      }

      if (sizeChanged) {
        this._options.dispatchResize();
      }

      this._options.requestUpdate();
    } finally {
      this._isResizing = false;
    }
  }

  prepareRendererDomElements(): void {
    const host = this._options.getBindElement();
    if (host) {
      if (window.getComputedStyle(host).position === "static") {
        host.style.position = "relative";
      }
      host.style.overflow = "hidden";
      host.style.minWidth = "0";
      host.style.minHeight = "0";
    }

    const canvas = this._options.renderer.instance.domElement;
    canvas.style.display = "block";
    canvas.style.position = "absolute";
    canvas.style.top = "0px";
    canvas.style.left = "0px";
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    [
      this._options.cssRenderer.domElement,
      this._options.cssRenderer3d.domElement,
    ].forEach((element) => {
      element.classList.add("css-renderer");
      element.style.position = "absolute";
      element.style.top = "0px";
      element.style.left = "0px";
      element.style.pointerEvents = "none";
    });
  }

  appendRendererDomElements(): void {
    const host = this._options.getBindElement();
    if (!host) return;

    host.appendChild(this._options.renderer.instance.domElement);
    host.appendChild(this._options.cssRenderer.domElement);
    this._options.setCssRendererAdded(true);
    host.appendChild(this._options.cssRenderer3d.domElement);
    this._options.setCssRenderer3dAdded(true);
  }

  /**
   * Drop the snapshot cover after the resized buffer has been presented.
   * Call at the start of the frame *after* the resize frame.
   */
  discardPresentedFreeze(): void {
    if (!this._coverUntilPresented) return;
    this._coverUntilPresented = false;
    this._uncover();
  }

  applyPendingResize(): void {
    if (!this._hasPendingObservedSize || !this._commitAllowed) return;
    this._commitAllowed = false;
    this.resize(false);
  }

  private _normalizeResizeDimension(value: number, fallback = 128): number {
    const size = Number.isFinite(value) && value > 0 ? value : fallback;
    return Math.max(1, Math.floor(size));
  }

  private _readResizeSize(): EngineResizeSize {
    const element =
      this._options.getBindElement() ??
      this._options.renderer.instance.domElement;
    const { width, height } = element.getBoundingClientRect();

    return {
      width: this._normalizeResizeDimension(width),
      height: this._normalizeResizeDimension(height),
    };
  }

  private _consumeResizeSize(): EngineResizeSize {
    if (this._hasPendingObservedSize) {
      this._hasPendingObservedSize = false;
      return {
        width: this._pendingObservedWidth,
        height: this._pendingObservedHeight,
      };
    }
    return this._readResizeSize();
  }

  private _ensureRendererDomAttached(): void {
    const host = this._options.getBindElement();
    if (!host) return;

    this.prepareRendererDomElements();

    const canvas = this._options.renderer.instance.domElement;
    if (canvas.parentElement !== host) {
      host.appendChild(canvas);
    }
  }

  private _scheduleObservedResize(width: number, height: number): void {
    const nextWidth = this._normalizeResizeDimension(width);
    const nextHeight = this._normalizeResizeDimension(height);
    const current = this._options.getSize();

    if (
      this._resizeInitialized &&
      nextWidth === current.width &&
      nextHeight === current.height
    ) {
      this._hasPendingObservedSize = false;
      this._commitAllowed = false;
      this._clearSettleTimer();
      return;
    }

    this._pendingObservedWidth = nextWidth;
    this._pendingObservedHeight = nextHeight;
    this._hasPendingObservedSize = true;
    this._commitAllowed = false;
    this._clearSettleTimer();
    this._settleTimer = window.setTimeout(() => {
      this._settleTimer = 0;
      this._commitAllowed = true;
      this._options.requestUpdate();
    }, EngineResizeController._SETTLE_MS);
  }

  private _clearSettleTimer(): void {
    if (!this._settleTimer) return;
    clearTimeout(this._settleTimer);
    this._settleTimer = 0;
  }

  private _coverWithLastFrame(): void {
    const src = this._options.renderer.instance.domElement;
    const host = this._options.getBindElement();
    if (!host || src.width < 2 || src.height < 2) return;

    if (!this._coverCanvas) {
      const cover = document.createElement("canvas");
      cover.setAttribute("aria-hidden", "true");
      cover.style.position = "absolute";
      cover.style.inset = "0";
      cover.style.width = "100%";
      cover.style.height = "100%";
      cover.style.display = "block";
      cover.style.pointerEvents = "none";
      this._coverCanvas = cover;
    }

    const dst = this._coverCanvas;
    try {
      dst.width = src.width;
      dst.height = src.height;
      const ctx = dst.getContext("2d");
      if (!ctx) return;
      ctx.drawImage(src, 0, 0);
      if (!this._coverHasOpaquePixel(ctx, src.width, src.height)) {
        this._uncover();
        return;
      }
      if (dst.parentElement !== host) src.after(dst);
    } catch {
      this._uncover();
    }
  }

  private _coverHasOpaquePixel(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
  ): boolean {
    const probes: Array<[number, number]> = [
      [0.5, 0.5],
      [0.25, 0.5],
      [0.5, 0.25],
      [0.75, 0.5],
    ];
    for (const [u, v] of probes) {
      const pixel = ctx.getImageData(
        Math.max(0, (width * u) | 0),
        Math.max(0, (height * v) | 0),
        1,
        1,
      ).data;
      if (pixel[3] > 8) return true;
    }
    return false;
  }

  private _uncover(): void {
    this._coverCanvas?.remove();
  }
}
